import { Injectable, NgZone, inject } from '@angular/core';
import { CapicheLoggerService } from 'capiche-logger';
import { CensorService } from '../censor/censor.service';
import { VoicePreferenceService } from '../voice-preference/voice-preference.service';
import { Subject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

type SpeakOptions = {
  id?: string; // correlate with a UI item (e.g., suggestionId)
  voiceName?: string;
  rate?: number; // 0.1–10
  pitch?: number; // 0–2
  volume?: number; // 0–1
  interrupt?: boolean; // if true, stop current and play now
};

interface QueueItem {
  id: string;
  utter: SpeechSynthesisUtterance;
}

@Injectable({ providedIn: 'root' })
export class TextToSpeechService {
  private censorService = inject(CensorService);
  private voicePreference = inject(VoicePreferenceService);
  private logger = new CapicheLoggerService('text-to-speech.service:', false);
  private zone = inject(NgZone);

  private queue: QueueItem[] = [];
  private current: QueueItem | null = null;
  private voices: SpeechSynthesisVoice[] = [];
  private ready = false;

  // Track completion and chains (e.g., title + definition under same id)
  private endedSubject = new Subject<string>();
  private pendingById = new Map<string, number>();

  constructor() {
    if (!('speechSynthesis' in window)) {
      this.logger.log('Web Speech not available');
      return;
    }

    const loadVoices = () => {
      this.voices = window.speechSynthesis.getVoices() || [];
      this.ready = this.voices.length > 0;
      this.logger.log('voices loaded:', this.voices.length);
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = () => this.zone.run(loadVoices);
  }

  /** Emits once when all utterances for a given id complete */
  ended$(id: string): Observable<void> {
    return this.endedSubject.asObservable().pipe(
      filter((doneId) => doneId === id),
      map(() => void 0),
    );
  }

  /** Add text to speak. Returns an id you can later cancel. */
  speak(text: string, opts: SpeakOptions = {}): string {
    const clean = this.censorService.mumbleOffensiveWords(text);
    const id = opts.id ?? globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

    const utter = new SpeechSynthesisUtterance(clean);

    const voiceName = opts.voiceName ?? this.voicePreference.selectedVoice?.name;
    if (voiceName && this.ready) {
      const v = this.voices.find((x) => x.name === voiceName);
      if (v) utter.voice = v;
    }
    if (opts.rate != null) utter.rate = opts.rate;
    if (opts.pitch != null) utter.pitch = opts.pitch;
    if (opts.volume != null) utter.volume = opts.volume;

    // Count utterances per id so we know when a chain has finished
    this.pendingById.set(id, (this.pendingById.get(id) ?? 0) + 1);

    utter.onend = () => this.zone.run(() => this.onUtterEnd(id));
    utter.onerror = () => this.zone.run(() => this.onUtterEnd(id));

    if (opts.interrupt) {
      window.speechSynthesis.cancel();
      this.queue = [];
      this.current = null;
    }

    this.queue.push({ id, utter });
    this.pump();
    return id;
  }

  /** Speak a suggestion and, if requested, its definition right after using the same id */
  speakSuggestionAndMaybeDefinition(
    suggestion: string,
    definition?: string,
    opts?: SpeakOptions & { includeDefinition?: boolean },
  ) {
    const id = this.speak(suggestion, { ...opts, interrupt: !!opts?.interrupt });
    if (opts?.includeDefinition && definition) {
      this.speak(definition, { ...opts, id, interrupt: false });
    }
    return id;
  }

  /** Global controls (Web Speech API is global) */
  pause() {
    if (speechSynthesis.speaking) speechSynthesis.pause();
  }
  resume() {
    if (speechSynthesis.paused) speechSynthesis.resume();
  }

  stopAll() {
    this.queue = [];
    this.current = null;
    window.speechSynthesis.cancel();
  }

  /** Best-effort per-id stop: cancel all, then re-enqueue survivors */
  stop(id: string) {
    const survivors = this.queue.filter((q) => q.id !== id);
    const isCurrent = this.current?.id === id;

    // Remove any pending count for this id so no ended$ fires spuriously
    this.pendingById.delete(id);

    if (isCurrent || survivors.length !== this.queue.length) {
      this.queue = [];
      this.current = null;
      window.speechSynthesis.cancel();

      // Requeue all that aren't the stopped id
      survivors.forEach((q) => this.queue.push(q));
      this.pump();
    }
  }

  // ---- internals ----
  private pump() {
    if (this.current || this.queue.length === 0) return;
    this.current = this.queue.shift()!;
    this.logger.log('speaking', this.current.id, this.current.utter.text);
    window.speechSynthesis.speak(this.current.utter);
  }

  private onUtterEnd(id: string) {
    if (this.current?.id === id) {
      this.current = null;
    }
    const remaining = (this.pendingById.get(id) ?? 1) - 1;
    if (remaining <= 0) {
      this.pendingById.delete(id);
      this.endedSubject.next(id);
    } else {
      this.pendingById.set(id, remaining);
    }
    this.pump();
  }
}
